import axios from 'axios';
import SystemCheck from '../utils/systemCheck';

// Ensure we're using the correct API endpoint
const EXPECTED_API_BASE = 'https://api.firstheritageinc.com/api';

// Validate API configuration on import
const validateApiConfig = () => {
  const apiCheck = SystemCheck.validateApiEndpoint();
  if (!apiCheck.isValid) {
    console.warn('API endpoint validation failed:', apiCheck);
  }
};

// Run validation
validateApiConfig();

// Create axios instance with base configuration
const api = axios.create({
  baseURL: EXPECTED_API_BASE,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'X-API-Version': '2.0.0', // Version header for tracking
  },
  timeout: 30000, // 30 second timeout
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => {
    // Log successful API calls to confirm we're using the right endpoint
    if (process.env.NODE_ENV === 'development') {
      console.log(`✅ API Success: ${response.config.method?.toUpperCase()} ${response.config.url}`);
    }
    return response;
  },
  (error) => {
    // Enhanced error handling with API endpoint validation
    if (error.code === 'NETWORK_ERROR' || error.message?.includes('Network Error')) {
      console.error('🔴 Network Error - Checking API endpoint:', EXPECTED_API_BASE);
      
      // Trigger system check if network error occurs
      const systemCheck = SystemCheck.checkSystemVersion();
      if (systemCheck.needsUpdate) {
        console.warn('System may need update due to network error');
      }
    }
    
    if (error.response?.status === 401) {
      // Token expired or invalid
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    
    // Log API errors for debugging
    if (process.env.NODE_ENV === 'development') {
      console.error(`🔴 API Error: ${error.config?.method?.toUpperCase()} ${error.config?.url}`, error.response?.data);
    }
    
    return Promise.reject(error);
  }
);

// Authentication API
export const authAPI = {
  login: async (credentials) => {
    const response = await api.post('/login', credentials);
    return response.data;
  },
  
  register: async (userData) => {
    console.log('authAPI.register: Sending data to API:', userData);
    const response = await api.post('/register', userData);
    console.log('authAPI.register: API response:', response.data);
    return response.data;
  },
  
  logout: async () => {
    const response = await api.post('/logout');
    return response.data;
  },
  
  getUser: async () => {
    const response = await api.get('/user');
    return response.data;
  },
  
  updateProfile: async (profileData) => {
    const response = await api.put('/profile', profileData);
    return response.data;
  },
  
  changePassword: async (passwordData) => {
    const response = await api.post('/change-password', passwordData);
    return response.data;
  },
  
  enableTwoFactor: async () => {
    const response = await api.post('/enable-2fa');
    return response.data;
  },
  
  disableTwoFactor: async () => {
    const response = await api.post('/disable-2fa');
    return response.data;
  },

  // Password reset methods
  forgotPassword: async (data) => {
    const response = await api.post('/forgot-password', data);
    return response.data;
  },

  resetPassword: async (data) => {
    const response = await api.post('/reset-password', data);
    return response.data;
  },

  validateResetToken: async (data) => {
    const response = await api.post('/validate-reset-token', data);
    return response.data;
  },

  // OTP methods
  verifyOtp: async (data) => {
    const response = await api.post('/verify-otp', data);
    return response.data;
  },

  resendOtp: async (data) => {
    const response = await api.post('/resend-otp', data);
    return response.data;
  }
};

// Bank Accounts API
export const accountsAPI = {
  getAccounts: async () => {
    const response = await api.get('/accounts');
    return response.data;
  },
  
  getAccount: async (accountId) => {
    const response = await api.get(`/accounts/${accountId}`);
    return response.data;
  },
  
  getBalance: async (accountId) => {
    const response = await api.get(`/accounts/${accountId}/balance`);
    return response.data;
  },
  
  getTransactions: async (accountId, params = {}) => {
    const response = await api.get(`/accounts/${accountId}/transactions`, { params });
    return response.data;
  }
};

// Transfers API
export const transferAPI = {
  // Get transfer history
  getTransfers: async (params = {}) => {
    const response = await api.get('/transfers', { params });
    return response.data;
  },
  
  // Get recent transfers for quick repeat
  getRecentTransfers: async (limit = 5) => {
    const response = await api.get('/transfers/recent', { params: { limit } });
    return response.data;
  },
  
  // Between user's own accounts
  betweenAccounts: async (transferData) => {
    const response = await api.post('/transfers/between-accounts', transferData);
    return response.data;
  },
  
      // To other First Heritage Community Bank customers
  sameBank: async (transferData) => {
    const response = await api.post('/transfers/same-bank', transferData);
    return response.data;
  },
  
  // To external banks (ACH)
  externalBank: async (transferData) => {
    const response = await api.post('/transfers/external-bank', transferData);
    return response.data;
  },
  
  // SEPA transfer (Single Euro Payments Area)
  sepa: async (transferData) => {
    const response = await api.post('/wire-transfers/sepa', transferData);
    return response.data;
  },
  
  // Get transfer details
  getTransfer: async (transferId) => {
    const response = await api.get(`/transfers/${transferId}`);
    return response.data;
  },
  
  // Cancel pending transfer
  cancelTransfer: async (transferId) => {
    const response = await api.post(`/transfers/${transferId}/cancel`);
    return response.data;
  },
  
  // Validate recipient for same bank transfer
  validateRecipient: async (emailOrPhone) => {
    const response = await api.post('/transfers/validate-recipient', { 
      recipient: emailOrPhone 
    });
    return response.data;
  },
  
  // Get transfer limits
  getLimits: async () => {
    const response = await api.get('/transfers/limits');
    return response.data;
  },
  
  // Verify external bank account
  verifyExternalAccount: async (accountData) => {
    const response = await api.post('/transfers/verify-external-account', accountData);
    return response.data;
  },
  
  // Repeat a previous transfer
  repeatTransfer: async (transferId, newAmount = null) => {
    const response = await api.post(`/transfers/${transferId}/repeat`, { 
      amount: newAmount 
    });
    return response.data;
  }
};

// Zelle API
export const zelleAPI = {
  getTransactions: async (params = {}) => {
    const response = await api.get('/zelle/transactions', { params });
    return response.data;
  },
  
  send: async (sendData) => {
    const response = await api.post('/zelle/send', sendData);
    return response.data;
  },
  
  request: async (requestData) => {
    const response = await api.post('/zelle/request', requestData);
    return response.data;
  },
  
  split: async (splitData) => {
    const response = await api.post('/zelle/split', splitData);
    return response.data;
  },
  
  getRecipients: async () => {
    const response = await api.get('/zelle/recipients');
    return response.data;
  },
  
  addRecipient: async (recipientData) => {
    const response = await api.post('/zelle/recipients', recipientData);
    return response.data;
  },
  
  getLimits: async () => {
    const response = await api.get('/zelle/limits');
    return response.data;
  }
};

// Wire Transfer API
export const wireTransferAPI = {
  getTransfers: async (params = {}) => {
    const response = await api.get('/wire-transfers', { params });
    return response.data;
  },
  
  domestic: async (transferData) => {
    const response = await api.post('/wire-transfers/domestic', transferData);
    return response.data;
  },
  
  international: async (transferData) => {
    const response = await api.post('/wire-transfers/international', transferData);
    return response.data;
  },
  
  sepa: async (transferData) => {
    const response = await api.post('/wire-transfers/sepa', transferData);
    return response.data;
  },
  
  getTransfer: async (transferId) => {
    const response = await api.get(`/wire-transfers/${transferId}`);
    return response.data;
  },
  
  cancel: async (transferId) => {
    const response = await api.post(`/wire-transfers/${transferId}/cancel`);
    return response.data;
  }
};

// Crypto API
export const cryptoAPI = {
  getWallets: async () => {
    const response = await api.get('/crypto/wallets');
    return response.data;
  },
  
  getPortfolio: async () => {
    const response = await api.get('/crypto/portfolio');
    return response.data;
  },
  
  buy: async (buyData) => {
    const response = await api.post('/crypto/buy', buyData);
    return response.data;
  },
  
  buyCrypto: async (buyData) => {
    const response = await api.post('/crypto/buy', buyData);
    return response.data;
  },
  
  sell: async (sellData) => {
    const response = await api.post('/crypto/sell', sellData);
    return response.data;
  },
  
  sellCrypto: async (sellData) => {
    const response = await api.post('/crypto/sell', sellData);
    return response.data;
  },
  
  getTransactions: async (params = {}) => {
    const response = await api.get('/crypto/transactions', { params });
    return response.data;
  },
  
  getMarketData: async () => {
    const response = await api.get('/crypto/market-data');
    return response.data;
  },
  
  getDepositMethods: async () => {
    const response = await api.get('/crypto/deposit-methods');
    return response.data;
  },
  
  deposit: async (depositData) => {
    const response = await api.post('/crypto/deposit', depositData);
    return response.data;
  },
  
  withdraw: async (withdrawData) => {
    const response = await api.post('/crypto/withdraw', withdrawData);
    return response.data;
  }
};


// Dashboard API
export const dashboardAPI = {
  getDashboard: async () => {
    const response = await api.get('/dashboard');
    return response.data;
  },
  
  getSummary: async () => {
    const response = await api.get('/dashboard/summary');
    return response.data;
  },
  
  getRecentTransactions: async (params = {}) => {
    const response = await api.get('/dashboard/recent-transactions', { params });
    return response.data;
  },
  
  getUpcomingPayments: async () => {
    const response = await api.get('/dashboard/upcoming-payments');
    return response.data;
  },
  
  getAccountOverview: async () => {
    const response = await api.get('/dashboard/account-overview');
    return response.data;
  },
  
  getComprehensiveHistory: async (params = {}) => {
    const response = await api.get('/dashboard/comprehensive-history', { params });
    return response.data;
  }
};

// Notifications API
export const notificationsAPI = {
  getNotifications: async (params = {}) => {
    const response = await api.get('/notifications', { params });
    return response.data;
  },
  
  summary: async () => {
    const response = await api.get('/notifications/summary');
    return response.data;
  },
  
  markAsRead: async (notificationId) => {
    const response = await api.post(`/notifications/${notificationId}/read`);
    return response.data;
  },
  
  markAllAsRead: async (params = {}) => {
    const response = await api.post('/notifications/read-all', params);
    return response.data;
  },
  
  deleteNotification: async (notificationId) => {
    const response = await api.delete(`/notifications/${notificationId}`);
    return response.data;
  },
  
  deleteReadNotifications: async (params = {}) => {
    const response = await api.delete('/notifications/read/all', { data: params });
    return response.data;
  },
  
  getPreferences: async () => {
    const response = await api.get('/notifications/preferences');
    return response.data;
  },
  
  updatePreferences: async (preferences) => {
    const response = await api.put('/notifications/preferences', preferences);
    return response.data;
  },
  
  createTest: async (testData) => {
    const response = await api.post('/notifications/test', testData);
    return response.data;
  }
};

// Cashback API
export const cashbackAPI = {
  getSummary: async () => {
    const response = await api.get('/cashback');
    return response.data;
  },
  
  getHistory: async (params = {}) => {
    const response = await api.get('/cashback/history', { params });
    return response.data;
  },
  
  getAvailable: async () => {
    const response = await api.get('/cashback/available');
    return response.data;
  },
  
  redeem: async (amount) => {
    const response = await api.post('/cashback/redeem', { amount });
    return response.data;
  },
  
  getByCategory: async () => {
    const response = await api.get('/cashback/by-category');
    return response.data;
  }
};

// Bill Payment API
export const billPaymentAPI = {
  // Payee management
  getPayees: async (params = {}) => {
    const response = await api.get('/bill-payments/payees', { params });
    return response.data;
  },
  
  createPayee: async (payeeData) => {
    const response = await api.post('/bill-payments/payees', payeeData);
    return response.data;
  },
  
  updatePayee: async (payeeId, payeeData) => {
    const response = await api.put(`/bill-payments/payees/${payeeId}`, payeeData);
    return response.data;
  },
  
  deletePayee: async (payeeId) => {
    const response = await api.delete(`/bill-payments/payees/${payeeId}`);
    return response.data;
  },
  
  // Payment management
  schedulePayment: async (paymentData) => {
    const response = await api.post('/bill-payments/schedule', paymentData);
    return response.data;
  },
  
  getPaymentHistory: async (params = {}) => {
    const response = await api.get('/bill-payments/history', { params });
    return response.data;
  },
  
  getScheduledPayments: async (params = {}) => {
    const response = await api.get('/bill-payments/scheduled', { params });
    return response.data;
  },
  
  getUpcomingBills: async (params = {}) => {
    const response = await api.get('/bill-payments/upcoming', { params });
    return response.data;
  },
  
  getPayment: async (paymentId) => {
    const response = await api.get(`/bill-payments/${paymentId}`);
    return response.data;
  },
  
  cancelPayment: async (paymentId) => {
    const response = await api.post(`/bill-payments/${paymentId}/cancel`);
    return response.data;
  },
  
  // Utility
  getCategories: async () => {
    const response = await api.get('/bill-payments/categories');
    return response.data;
  }
};

// Support API
export const supportAPI = {
  // Get user's tickets
  getTickets: async (params = {}) => {
    const response = await api.get('/support/tickets', { params });
    return response.data;
  },
  
  // Create new ticket
  createTicket: async (ticketData) => {
    const response = await api.post('/support/tickets', ticketData);
    return response.data;
  },
  
  // Get specific ticket
  getTicket: async (ticketId) => {
    const response = await api.get(`/support/tickets/${ticketId}`);
    return response.data;
  },
  
  // Add reply to ticket
  addReply: async (ticketId, replyData) => {
    const response = await api.post(`/support/tickets/${ticketId}/reply`, replyData);
    return response.data;
  },
  
  // Update ticket status
  updateStatus: async (ticketId, status) => {
    const response = await api.put(`/support/tickets/${ticketId}/status`, { status });
    return response.data;
  },
  
  // Get support dashboard
  getDashboard: async () => {
    const response = await api.get('/support/dashboard');
    return response.data;
  },
  
  // Get categories
  getCategories: async () => {
    const response = await api.get('/support/categories');
    return response.data;
  },
  
  // Search tickets
  search: async (query, limit = 10) => {
    const response = await api.get('/support/search', { 
      params: { query, limit } 
    });
    return response.data;
  }
};

// Countries API
export const countriesAPI = {
  // Get all countries for dropdown/selection
  getCountries: async () => {
    const response = await api.get('/countries');
    return response.data;
  },
  
  // Get specific country by ID
  getCountry: async (countryId) => {
    const response = await api.get(`/countries/${countryId}`);
    return response.data;
  },
  
  // Get countries by currency code
  getCountriesByCurrency: async (currencyCode) => {
    const response = await api.get(`/countries/currency/${currencyCode}`);
    return response.data;
  },
  
  // Get country by country code
  getCountryByCode: async (code) => {
    const response = await api.get(`/countries/code/${code}`);
    return response.data;
  },
  
  // Search countries by name
  searchCountries: async (query) => {
    const response = await api.get('/countries/search', { 
      params: { q: query } 
    });
    return response.data;
  }
};

// Cards API
export const cardsAPI = {
  // Get user's cards
  getCards: async (params = {}) => {
    const response = await api.get('/cards', { params });
    return response.data;
  },
  
  // Get specific card details
  getCard: async (cardId) => {
    const response = await api.get(`/cards/${cardId}`);
    return response.data;
  },
  
  // Apply for a new card
  apply: async (cardData) => {
    const response = await api.post('/cards', cardData);
    return response.data;
  },
  
  // Get card types
  getCardTypes: async () => {
    const response = await api.get('/cards/types');
    return response.data;
  },
  
  // Get ATM locations
  getAtmLocations: async (params = {}) => {
    const response = await api.get('/cards/atm-locations', { params });
    return response.data;
  },
  
  // Activate card
  activate: async (cardId, activationData) => {
    const response = await api.post(`/cards/${cardId}/activate`, activationData);
    return response.data;
  },
  
  // Toggle card lock/unlock
  toggleLock: async (cardId, data = {}) => {
    const response = await api.post(`/cards/${cardId}/toggle-lock`, data);
    return response.data;
  },
  
  // Report lost/stolen card
  reportLostStolen: async (cardId, data) => {
    const response = await api.post(`/cards/${cardId}/report-lost`, data);
    return response.data;
  },
  
  // Update card limits
  setLimits: async (cardId, limits) => {
    const response = await api.put(`/cards/${cardId}/limits`, limits);
    return response.data;
  },
  
  // Update card settings
  updateSettings: async (cardId, settings) => {
    const response = await api.put(`/cards/${cardId}/settings`, settings);
    return response.data;
  },
  
  // Change PIN
  changePin: async (cardId, pinData) => {
    const response = await api.post(`/cards/${cardId}/change-pin`, pinData);
    return response.data;
  },
  
  // Get card transactions
  getTransactions: async (cardId, params = {}) => {
    const response = await api.get(`/cards/${cardId}/transactions`, { params });
    return response.data;
  }
};

export default api; 